import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import {
    SizerGenericResponse,
    PolicyData,
    PostOpportunityDataDTOResponse,
    PostSiteDataDTO,
    PostSizerData,
    VmData,
    VmDataResponse,
    OpportunityData,
    TCOData,
    Vm,
} from '../models/disaster-recovery-assessment-model';

@Injectable({
    providedIn: 'root',
})
export class DisasterRecoveryAssessmentService {
    private getEndpoint(endpoint: string, fileId?: string): string {
        const baseUrl = '/pure-protect-sizer/pure-protect-sizer';

        switch (endpoint) {
            case 'opportunity':
            case 'site':
                return `${baseUrl}/${endpoint}/`;

            case 'vm':
            case 'policy':
            case 'sizing':
            case 'tco':
                return `${baseUrl}/${endpoint}/?file_id=${fileId}`;

            default:
                return `${baseUrl}/${endpoint}`;
        }
    }

    constructor(private http: HttpClient) {}

    private camelToSnakeCase(obj: any): any {
        if (Array.isArray(obj)) {
            return obj.map(item => this.camelToSnakeCase(item));
        } else if (obj !== null && obj.constructor === Object) {
            return Object.keys(obj).reduce((acc, key) => {
                const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
                acc[snakeKey] = this.camelToSnakeCase(obj[key]);
                return acc;
            }, {} as any);
        }
        return obj;
    }

    private snakeToCamelCase(obj: any): any {
        if (Array.isArray(obj)) {
            return obj.map(item => this.snakeToCamelCase(item));
        } else if (obj !== null && obj.constructor === Object) {
            return Object.keys(obj).reduce((acc, key) => {
                const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
                acc[camelKey] = this.snakeToCamelCase(obj[key]);
                return acc;
            }, {} as any);
        }
        return obj;
    }

    postSizerData(fileId: string, vms: Vm[]): Observable<PostSizerData> {
        const payload = {
            fileId: fileId,
            uploadSpeed: 1000,
            cloudUploadSpeed: 620,
            isThrottling: false,
            throttlingBandwidth: '',
            vms: vms.map(vm => {
                vm.protectedChk = true;
                return vm;
            }),
        };

        const snakeCasePayload = this.camelToSnakeCase(payload);
        return this.http
            .post<PostSizerData>(this.getEndpoint('sizing', fileId), snakeCasePayload)
            .pipe(map(res => this.snakeToCamelCase(res)));
    }

    postTCOData(fileId: string): Observable<TCOData> {
        const payload = {
            fileId: fileId,
            awsRegion: 'us-east-2',
            supportPlan: 'Standard',
            paymentTerm: 'One Year Annual',
            scaleDown: false,
        };

        const snakeCasePayload = this.camelToSnakeCase(payload);
        return this.http
            .post<TCOData>(this.getEndpoint('tco', fileId), snakeCasePayload)
            .pipe(map(res => this.snakeToCamelCase(res)));
    }

    getVmData(fileId: string): Observable<VmData> {
        return this.http
            .get<VmDataResponse>(this.getEndpoint('vm', fileId))
            .pipe(map(res => this.snakeToCamelCase(res.data)));
    }

    getPolicyData(fileId: string): Observable<PolicyData[]> {
        return this.http
            .get<PolicyData[]>(this.getEndpoint('policy', fileId))
            .pipe(map(res => this.snakeToCamelCase(res)));
    }

    getOpportunity(): Observable<OpportunityData> {
        return this.http
            .get<OpportunityData>(this.getEndpoint('opportunity'))
            .pipe(map(res => this.snakeToCamelCase(res)));
    }

    postOpportunityData(accountName: string, name: string): Observable<PostOpportunityDataDTOResponse> {
        const body: PostOpportunityDataDTOResponse = {
            accountName: accountName,
            name: name,
        };
        const snakeCaseBody = this.camelToSnakeCase(body);
        return this.http
            .post<SizerGenericResponse>(this.getEndpoint('opportunity'), snakeCaseBody)
            .pipe(map(res => this.snakeToCamelCase(res.data)));
    }

    postSiteData(
        file: File,
        timestamp: string,
        id: string,
        name: string,
    ): Observable<PostSiteDataDTO | PostOpportunityDataDTOResponse> {
        const formData = new FormData();
        const siteData: PostSiteDataDTO = {
            file: file,
            timestamp: timestamp,
            opportunity: id,
            name: name,
        };
        formData.append('file', siteData.file);
        formData.append('timestamp', siteData.timestamp);
        formData.append('opportunity', siteData.opportunity);
        formData.append('name', siteData.name);

        return this.http
            .post<SizerGenericResponse>(this.getEndpoint('site'), formData)
            .pipe(map(res => this.snakeToCamelCase(res.data)));
    }
}
